import React from "react";
import { PageHeader } from "@/components/PageHeader";
import {
  MapPin, ChevronRight, ChevronLeft, Phone, Check, Home, Euro, Unlock, CloudOff, Download, CheckCircle2,
} from "lucide-react";

export function StopListView({ selectedGiro, stops, onBack, onSelectStop, isAdmin, onUnlockStop, prefetchState }) {
  const pf = prefetchState || { active: false, done: 0, total: 0, ready: false };
  const pct = pf.total > 0 ? Math.round((pf.done / pf.total) * 100) : 0;
  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6">
        <PageHeader title={`Giro ${selectedGiro.cod_agente}-${selectedGiro.cod_giorno}`} />
        <div className="flex items-center gap-3 mb-4">
          <button onClick={onBack}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors" data-testid="back-to-giri">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold text-white">Giro {selectedGiro.cod_agente}-{selectedGiro.cod_giorno}</h1>
            <p className="text-zinc-400 text-xs">
              {stops.length} fermate
              {(() => {
                const done = stops.filter(s => s.completed_order_id).length;
                return done > 0 ? (
                  <span className="text-emerald-400 ml-2">• {done} fatte</span>
                ) : null;
              })()}
            </p>
          </div>
        </div>

        {/* Offline prefetch progress banner */}
        {(pf.active || pf.ready) && pf.total > 0 && (
          <div
            data-testid="prefetch-progress"
            className={`mb-4 rounded-lg border p-2.5 ${
              pf.ready
                ? "border-emerald-500/30 bg-emerald-500/10"
                : "border-blue-500/30 bg-blue-500/10"
            }`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              {pf.ready ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              ) : (
                <Download className="w-4 h-4 text-blue-400 shrink-0 animate-pulse" />
              )}
              <span className={`text-xs font-medium flex-1 ${pf.ready ? "text-emerald-300" : "text-blue-300"}`}>
                {pf.ready
                  ? `✓ Giro pronto per uso offline (${pf.done}/${pf.total} fermate)`
                  : `Scaricamento offline in corso… ${pf.done}/${pf.total} fermate`}
              </span>
              <span className={`text-xs font-mono font-bold ${pf.ready ? "text-emerald-400" : "text-blue-400"}`}>
                {pct}%
              </span>
            </div>
            <div className="h-1.5 w-full bg-black/30 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${pf.ready ? "bg-emerald-500" : "bg-blue-500"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          {stops.map((stop, idx) => {
            const hasDestinazione = stop.denominaz_1 && stop.denominaz_1 !== stop.denominaz;
            // denominaz = nome destinazione (es. "Laboratorio"); denominaz_1 = ragione sociale cliente (es. "DOMUS CARNE S.N.C.")
            const nameToShow = stop.denominaz || stop.ragione_sociale || stop.denominaz_1;
            const clientLabel = hasDestinazione ? stop.denominaz_1 : null;
            const addressLine = [stop.indirizzo, stop.localita].filter(Boolean).join(", ");
            const isDone = !!stop.completed_order_id;
            const isPendingOffline = !isDone && !!stop.pending_offline;
            return (
              <button key={stop.id}
                data-testid={`stop-${stop.id}`}
                onClick={() => onSelectStop(stop)}
                className={`w-full p-3 rounded-lg border text-left transition-colors flex items-start gap-3 ${
                  isDone
                    ? "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50"
                    : isPendingOffline
                      ? "border-amber-500/40 bg-amber-500/5 hover:border-amber-500/60"
                      : "border-white/5 hover:border-amber-500/30 bg-[#18181b]"
                }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                  isDone ? "bg-emerald-500/20" : isPendingOffline ? "bg-amber-500/20" : "bg-amber-500/10"
                }`}>
                  {isDone
                    ? <Check className="w-4 h-4 text-emerald-400" />
                    : isPendingOffline
                      ? <CloudOff className="w-4 h-4 text-amber-300" />
                      : <span className="text-amber-400 text-xs font-bold">{idx + 1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {hasDestinazione && <Home className="w-3 h-3 text-cyan-400 shrink-0" />}
                    <p className={`text-sm font-medium truncate ${isDone ? "text-emerald-300" : isPendingOffline ? "text-amber-200" : "text-white"}`}>{nameToShow}</p>
                  </div>
                  {hasDestinazione && (
                    <p className="text-amber-400 text-[10px] font-bold truncate">cliente: {clientLabel}</p>
                  )}
                  {addressLine && (
                    <div className="flex items-start gap-1 text-[10px] text-zinc-400 mt-0.5">
                      <MapPin className="w-3 h-3 shrink-0 mt-px" />
                      <span className="truncate">{addressLine}</span>
                    </div>
                  )}
                  {stop.telefono && (
                    <div className="flex items-center gap-1 text-[10px] text-zinc-500 mt-0.5">
                      <Phone className="w-3 h-3 shrink-0" />
                      <span>{stop.telefono}</span>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0 mt-0.5 flex flex-col items-end gap-0.5">
                  {isDone ? (
                    <span className="text-emerald-400 text-[10px] font-semibold">✓ FATTO</span>
                  ) : isPendingOffline ? (
                    <span className="text-amber-300 text-[10px] font-semibold">IN CODA</span>
                  ) : (
                    <span className="text-zinc-500 text-[10px]">{stop.prodotti?.length || 0} art.</span>
                  )}
                  {isDone && isAdmin && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`unlock-stop-${stop.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("Sbloccare questa presa ordine? La fermata tornerà disponibile.")) {
                          onUnlockStop?.(stop);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          if (window.confirm("Sbloccare questa presa ordine? La fermata tornerà disponibile.")) {
                            onUnlockStop?.(stop);
                          }
                        }
                      }}
                      title="Sblocca presa ordine (admin)"
                      className="inline-flex items-center gap-0.5 text-amber-300 text-[9px] font-semibold bg-amber-500/15 border border-amber-500/40 rounded px-1 py-[1px] hover:bg-amber-500/25 cursor-pointer"
                    >
                      <Unlock className="w-2.5 h-2.5" />
                      Sblocca
                    </span>
                  )}
                  {Number(stop.saldo_aperto) > 0 && (
                    <span
                      data-testid={`stop-saldo-${stop.id}`}
                      title={`${stop.n_fatture_aperte} fatture da incassare`}
                      className="inline-flex items-center gap-0.5 text-orange-300 text-[9px] font-mono font-bold bg-orange-500/15 border border-orange-500/40 rounded px-1 py-[1px]"
                    >
                      <Euro className="w-2.5 h-2.5" />
                      {stop.saldo_aperto.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                  {stop.tipo_listino === 2 && (
                    <span
                      data-testid={`stop-listino2-${stop.id}`}
                      title="Listino 2 dedicato"
                      className="text-cyan-300 text-[9px] font-mono font-bold bg-cyan-500/10 border border-cyan-500/30 rounded px-1"
                    >
                      L2
                    </span>
                  )}
                  {Number(stop.sconto_canale) > 0 && (
                    <span className="text-violet-300 text-[9px] font-mono bg-violet-500/10 border border-violet-500/30 rounded px-1">
                      Sc.{stop.sconto_canale}%
                    </span>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0 mt-1.5" />
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
